import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Modal from '../../../components/orizon-ui/Modal';
import StatusPill from '../../../components/orizon-ui/StatusPill';
import { FormSection, TextareaField, InfoBox } from '../../../components/orizon-ui/FormField';
import FormModal, { useFormSalva, useSporco } from '../../../components/gru/FormModal';
import {
  formattaQta, formattaDataOra, formattaData, ETICHETTE_MOVIMENTO,
} from '../../../components/gru/scorta';
import { gruStornaMovimento, nuovaChiaveIdempotenza } from '../../../lib/services/gruService';

export const unitaDi = (riga) => String(riga.unita_key ?? '').toUpperCase();

const Campo = ({ label, valore, mono = false }) => (
  <div className="flex flex-col gap-1.5 min-w-0">
    <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/50 select-none truncate">{label}</span>
    <span className={`text-[15px] font-bold text-foreground/90 break-words ${mono ? 'font-mono tracking-tight' : ''}`}>
      {valore === undefined || valore === null || valore === '' ? '---' : valore}
    </span>
  </div>
);

const descrizioneComponente = (r) => {
  if (!r.componente_id) return null;
  return `${r.reparto_label ?? ''}${r.componente_posizione ? ` ${r.componente_posizione}` : ''}`.trim() || null;
};

/** Cosa succede stornando un movimento di questo tipo (testo mostrato nel form di storno). */
const effettoStorno = (riga) => {
  const base = 'La giacenza viene riportata com\'era prima di questo movimento con un movimento opposto (non si cancella nulla).';
  if (riga.tipo === 'sostituzione' || riga.tipo === 'rientrato') {
    return `${base} Anche l'intervento collegato risulterà annullato.`;
  }
  if (riga.tipo === 'carico' && riga.intervento_id) {
    return `${base} L'ordine collegato tornerà "In ordine".`;
  }
  return base;
};

// ─── Form di storno ──────────────────────────────────────────────────────────

const FormStorno = ({ movimento, onClose, onSaved }) => {
  const [chiave] = useState(() => nuovaChiaveIdempotenza());
  const [motivo, setMotivo] = useState('');
  const { salvando, errori, impostaErrori, pulisci, salva } = useFormSalva();
  const sporco = useSporco({ motivo });

  const invia = async () => {
    if (motivo.trim() === '') {
      impostaErrori({ motivo: 'Il motivo è obbligatorio' });
      return;
    }
    await salva(
      () => gruStornaMovimento({ chiave, movimentoId: movimento.id, motivo: motivo.trim() }),
      (r) => `Movimento stornato: giacenza ${formattaQta(r.qta, 3)} ${unitaDi(movimento)}.`,
      onSaved,
    );
  };

  return (
    <FormModal
      titolo="Storna movimento"
      etichetta={`Storico · Movimento #${movimento.id}`}
      larghezza="md"
      sporco={sporco}
      salvando={salvando}
      salvaLabel="Storna"
      onClose={onClose}
      onSubmit={invia}
    >
      <div className="flex items-center justify-between gap-3 p-4 rounded-2xl bg-muted/20 border border-border/40">
        <div className="min-w-0">
          <p className="text-sm font-black uppercase tracking-tight text-foreground break-words">{movimento.nome}</p>
          <p className="text-[10px] font-mono font-bold text-muted-foreground mt-0.5">
            {ETICHETTE_MOVIMENTO[movimento.tipo]?.label ?? movimento.tipo} · {Number(movimento.qta_delta) > 0 ? '+' : ''}
            {formattaQta(movimento.qta_delta, 3)} {unitaDi(movimento)}
          </p>
        </div>
      </div>

      <InfoBox tono="warn">{effettoStorno(movimento)}</InfoBox>

      <TextareaField
        label="Motivo dello storno" name="motivo" value={motivo}
        onChange={(v) => { setMotivo(v); pulisci('motivo'); }}
        placeholder="es. Errore di inserimento, articolo sbagliato..."
        obbligatorio errore={errori.motivo} righe={3}
      />
    </FormModal>
  );
};

// ─── Dettaglio ───────────────────────────────────────────────────────────────

/** Dettaglio di un movimento dello Storico, con la possibilità di stornarlo. */
const DettaglioMovimento = ({ movimento, onClose, onAggiornato }) => {
  const [stornoAperto, setStornoAperto] = useState(false);
  const et = ETICHETTE_MOVIMENTO[movimento.tipo] ?? { label: movimento.tipo, tono: 'neutro' };
  const positivo = Number(movimento.qta_delta) > 0;
  const stornabile = movimento.tipo !== 'storno' && !movimento.stornato;
  const unita = unitaDi(movimento);

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        titolo={movimento.nome}
        etichetta={`Movimento #${movimento.id}`}
        larghezza="lg"
        bloccaChiusura={stornoAperto}
        footer={stornabile ? (
          <button
            type="button"
            onClick={() => setStornoAperto(true)}
            className="flex items-center gap-2 px-6 py-3 bg-red-500/10 text-red-500 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-red-500 hover:text-white transition-all border border-red-500/20 shadow-lg shadow-red-500/10"
          >
            <Icon name="Undo2" size={14} />
            Storna movimento
          </button>
        ) : null}
      >
        <div className="flex flex-wrap items-center gap-3">
          <StatusPill tono={et.tono} label={et.label} />
          {movimento.stornato && <StatusPill tono="warn" label="Stornato" />}
        </div>

        <FormSection titolo="Movimento" icona="ArrowRightLeft">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">Movimentato</span>
              <span className={`text-4xl font-black tabular-nums ${positivo ? 'text-emerald-500' : 'text-red-500'}`}>
                {positivo ? '+' : ''}{formattaQta(movimento.qta_delta, 3)}
              </span>
              <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">{unita}</span>
            </div>
            <Campo label="Giacenza prima" valore={`${formattaQta(movimento.qta_prima, 3)} ${unita}`} />
            <Campo label="Giacenza dopo" valore={`${formattaQta(movimento.qta_dopo, 3)} ${unita}`} />
            <Campo label="Data e ora" valore={formattaDataOra(movimento.created_at)} />
          </div>
          <div className="grid grid-cols-2 gap-y-8 gap-x-4">
            <Campo label="Codice" valore={movimento.ean} mono />
            <Campo label="Registrato da" valore={movimento.utente_nome} />
          </div>
        </FormSection>

        {movimento.intervento_id && (
          <FormSection titolo="Intervento collegato" icona="Wrench">
            <div className="grid grid-cols-2 gap-y-8 gap-x-4">
              <Campo label="Data intervento" valore={formattaData(movimento.data_intervento)} />
              <Campo label="Gru" valore={movimento.gru_codice} mono />
              <Campo label="Componente" valore={descrizioneComponente(movimento)} />
              <Campo label="Causa" valore={movimento.causa_label} />
              <Campo label="Stato intervento" valore={movimento.intervento_stato} />
            </div>
          </FormSection>
        )}

        {movimento.ordine_collegato_id && (
          <FormSection titolo="Ordine collegato" icona="ClipboardList">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-4">
              <Campo label="Ordinati" valore={`${formattaQta(movimento.ordine_qta, 3)} ${unita}`} />
              <Campo label="Ricevuti" valore={`${formattaQta(movimento.ordine_qta_ricevuta, 3)} ${unita}`} />
              <Campo label="Residuo" valore={`${formattaQta(Math.max(0, Number(movimento.ordine_qta) - Number(movimento.ordine_qta_ricevuta)), 3)} ${unita}`} />
              <div className="flex flex-col gap-1.5 min-w-0">
                <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/50 select-none truncate">Stato ordine</span>
                <div>
                  <StatusPill
                    tono={movimento.ordine_stato === 'ricevuto' ? 'info' : movimento.ordine_stato === 'annullato' ? 'neutro' : 'warn'}
                    label={movimento.ordine_stato === 'ricevuto' ? 'Ricevuto' : movimento.ordine_stato === 'annullato' ? 'Annullato' : 'In ordine'}
                  />
                </div>
              </div>
            </div>
          </FormSection>
        )}

        {movimento.storno_di && (
          <InfoBox>Questo movimento è lo storno del movimento #{movimento.storno_di}.</InfoBox>
        )}

        <FormSection titolo="Nota" icona="FileText">
          {movimento.motivo
            ? <p className="text-sm font-medium text-foreground/70 leading-relaxed italic">"{movimento.motivo}"</p>
            : <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40 italic">Nessuna nota presente.</p>}
        </FormSection>
      </Modal>

      {stornoAperto && (
        <FormStorno
          movimento={movimento}
          onClose={() => setStornoAperto(false)}
          onSaved={() => {
            setStornoAperto(false);
            onAggiornato();
            onClose();
          }}
        />
      )}
    </>
  );
};

export default DettaglioMovimento;
